import { useEffect, useRef, useState } from "react";
import { PageHeader, Empty } from "@/components/UIBits";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const feedRef = useRef(null);
  const [autoscroll, setAutoscroll] = useState(true);

  const load = async () => {
    try {
      const fresh = await api.listLogs(300);
      setLogs(fresh);
    } catch {/* ignore */}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (autoscroll && feedRef.current) {
      feedRef.current.scrollTop = 0;
    }
  }, [logs, autoscroll]);

  const clear = async () => {
    if (!window.confirm("Clear all logs?")) return;
    await api.clearLogs();
    toast.success("Logs cleared");
    load();
  };

  return (
    <div data-testid="logs-page">
      <PageHeader
        title="Live Logs"
        subtitle={`// ${logs.length} entries — terminal stream`}
        testid="logs-header"
        actions={
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              data-testid="logs-autoscroll"
              onClick={() => setAutoscroll((v) => !v)}
              className="font-mono text-xs text-zinc-400 hover:text-white"
            >
              autoscroll: {autoscroll ? "ON" : "OFF"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="logs-clear"
              onClick={clear}
              className="border-[#27272a] text-zinc-400 hover:bg-[#18181b]"
            >
              clear
            </Button>
          </div>
        }
      />

      {logs.length === 0 ? (
        <Empty testid="logs-empty">Stream is empty</Empty>
      ) : (
        <div
          ref={feedRef}
          className="terminal-feed p-4 h-[70vh] overflow-y-auto"
          data-testid="logs-feed"
        >
          {logs.map((l) => (
            <LogRow key={l.id} log={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function LogRow({ log }) {
  const dirColor = {
    inbound: "text-blue-400",
    outbound: "text-emerald-400",
    system: "text-zinc-400",
    blocked: "text-red-400",
    burn: "text-red-500",
    vcard: "text-amber-400",
  }[log.direction] || "text-zinc-400";

  const ts = new Date(log.timestamp).toLocaleTimeString();
  return (
    <div className="line py-1 px-2 flex gap-3 items-start" data-testid={`log-${log.id}`}>
      <span className="text-zinc-600 shrink-0 w-20">{ts}</span>
      <span className={`shrink-0 uppercase w-20 ${dirColor}`}>[{log.direction}]</span>
      <span className="text-zinc-300 break-all">
        {log.from_number && <span className="text-zinc-500">{log.from_number}</span>}
        {log.to_number && <span className="text-zinc-500"> → {log.to_number}</span>}
        {log.text && <span className="text-white"> · {log.text}</span>}
        {log.note && <span className="text-zinc-500"> // {log.note}</span>}
      </span>
    </div>
  );
}
